import { MenuRadioItemElement } from "../menu/radio-item.js";
import { cacheKey } from "../../i18n/cache-key.js";
import { MenuItemIndicatorElement } from "../menu/item-indicator.js";
import { applyElementProps } from "@videojs/core/dom";
import { translateText } from "@videojs/core/i18n";
import { cloneTemplateRoot, getTemplateElement, getTemplateRoot } from "@videojs/utils/dom";
//#region src/ui/radio-options/controller.ts
/** Renders normalized options into menu radio items and manages their interaction lifecycle. */
var RadioOptionsController = class {
	#host;
	#config;
	#contentKey = "";
	#translator = null;
	#disconnect = null;
	constructor(host, config) {
		this.#host = host;
		this.#config = config;
		host.addController(this);
	}
	hostConnected() {
		this.#disconnect = new AbortController();
		this.#host.addEventListener("value-change", this.#handleValueChange, { signal: this.#disconnect.signal });
	}
	hostDisconnected() {
		this.#disconnect?.abort();
		this.#disconnect = null;
	}
	hostDestroyed() {
		this.hostDisconnected();
	}
	sync(state, translator, locale) {
		this.#host.value = state.value;
		applyElementProps(this.#host, {
			"aria-disabled": state.disabled ? "true" : void 0,
			hidden: state.hidden ? "" : void 0
		});
		const template = getTemplateElement(this.#host);
		const templateRoot = template ? getTemplateRoot(template) : null;
		const itemRoot = templateRoot?.localName === MenuRadioItemElement.tagName ? templateRoot : null;
		const contentKey = `${state.options.map((option) => `${option.value}:${cacheKey(option.label, option.labelParams)}:${this.#config.getOptionCacheKey?.(option) ?? ""}`).join("|")}::${locale}::${template?.innerHTML ?? ""}`;
		if (contentKey !== this.#contentKey || translator !== this.#translator) {
			this.#contentKey = contentKey;
			this.#translator = translator;
			for (const child of [...this.#host.children]) {
				if (child === template) continue;
				child.remove();
			}
			const items = state.options.map((option) => {
				const item = itemRoot ? cloneTemplateRoot(itemRoot, this.#host.ownerDocument) : this.#host.ownerDocument.createElement(MenuRadioItemElement.tagName);
				item.value = option.value;
				this.#config.setItemAttributes?.(item, option);
				const label = translateText(option.label, translator, option.labelParams);
				if (this.#config.renderItem) this.#config.renderItem(item, label, option);
				else this.#setItemLabel(item, label);
				return item;
			});
			this.#host.append(...items);
		}
		const optionsByValue = new Map(state.options.map((option) => [option.value, option]));
		for (const item of this.#host.querySelectorAll(MenuRadioItemElement.tagName)) {
			const checked = item.value === state.value;
			const option = optionsByValue.get(item.value);
			item.disabled = state.disabled || option?.disabled === true;
			for (const indicator of item.querySelectorAll(MenuItemIndicatorElement.tagName)) indicator.checked = checked;
		}
	}
	#handleValueChange = (event) => {
		if (event.target !== this.#host) return;
		const { value } = event.detail;
		this.#config.onValueChange(value);
	};
	#setItemLabel(item, label) {
		const labelPart = item.querySelector("[data-part~=\"label\"]");
		if (labelPart) labelPart.textContent = label;
		else item.textContent = label;
	}
};
//#endregion
export { RadioOptionsController };

//# sourceMappingURL=controller.js.map